import { ActionTree } from 'vuex';
import axios, { AxiosProgressEvent } from 'axios';

import videoArchiveApi from '@/api/videoArchive';

import {
  IArchiveParams,
  IDownloadInfo,
  IDownloadStartInfo,
  IDownloadProgressInfo,
  IDownloadTaskParams,
  IArchiveStreamParams,
  IArchiveStreamResponse,
  IArchiveListResponse,
} from '@/@types/api/videoArchive';

interface IDownloadFilePayload {
  params: IDownloadTaskParams;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

interface IDownloadFileByUrlPayload {
  downloadUrl: string;
  totalSize: number;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

const getFileNameFromContentDisposition = (
  contentDisposition?: string,
): string => {
  if (!contentDisposition) return 'archive.mp4';

  const encodedFileNameMatch = contentDisposition.match(
    /filename\*=UTF-8''(.+)/i,
  );

  if (encodedFileNameMatch?.[1]) {
    return decodeURIComponent(encodedFileNameMatch[1].replace(/["']/g, ''));
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

  if (fileNameMatch?.[1]) {
    return fileNameMatch[1];
  }

  return 'archive.mp4';
};

const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = blobUrl;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(blobUrl);
};

export default {
  actions: <ActionTree<unknown, any>>{
    async getArchiveList(_, params: IArchiveParams): Promise<IArchiveListResponse> {
      try {
        return await videoArchiveApi.getArchiveList(params);
      } catch (error) {
        console.error('Error fetching archive list:', error);
        throw error;
      }
    },

    async archiveStream(_, params: IArchiveStreamParams): Promise<IArchiveStreamResponse> {
      try {
        return await videoArchiveApi.archiveStream(params);
      } catch (error) {
        console.error('Error fetching archive stream:', error);
        throw error;
      }
    },

    async getArchiveCalendar(
      _,
      params: IArchiveParams,
    ): Promise<IDownloadInfo> {
      try {
        return await videoArchiveApi.getArchiveCalendar(params);
      } catch (error) {
        console.error('Error fetching archive calendar:', error);
        throw error;
      }
    },

    async downloadInfo(_, params: IArchiveParams): Promise<IDownloadInfo> {
      try {
        return await videoArchiveApi.downloadInfo(params);
      } catch (error) {
        console.error('Error fetching download info:', error);
        throw error;
      }
    },

    async downloadStart(
      _,
      params: IArchiveParams,
    ): Promise<IDownloadStartInfo> {
      try {
        return await videoArchiveApi.downloadStart(params);
      } catch (error) {
        console.error('Error starting download:', error);
        throw error;
      }
    },

    async downloadProgress(
      _,
      params: IDownloadTaskParams,
    ): Promise<IDownloadProgressInfo> {
      try {
        return await videoArchiveApi.downloadProgress(params);
      } catch (error) {
        console.error('Error fetching download progress:', error);
        throw error;
      }
    },

    async downloadFile(_, payload: IDownloadFilePayload): Promise<void> {
      try {
        await videoArchiveApi.downloadFile(
          payload.params,
          payload.onProgress,
          payload.signal,
        );
      } catch (error) {
        console.error('Error downloading file:', error);
        throw error;
      }
    },

    async downloadCancel(
      _,
      params: IDownloadTaskParams,
    ): Promise<IDownloadProgressInfo> {
      try {
        return await videoArchiveApi.downloadCancel(params);
      } catch (error) {
        console.error('Error cancelling download:', error);
        throw error;
      }
    },

    async downloadFileByUrl(
      _,
      payload: IDownloadFileByUrlPayload,
    ): Promise<void> {
      try {
        const response = await axios.get<Blob>(payload.downloadUrl, {
          responseType: 'blob',
          signal: payload.signal,
          onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
            const loaded = progressEvent.loaded || 0;

            const total = payload.totalSize || progressEvent.total || 0;

            if (!total) return;

            const percent = Math.round((loaded * 100) / total);

            payload.onProgress?.(Math.min(percent, 100));
          },
        });

        const contentDisposition = response.headers['content-disposition'];
        const fileName = getFileNameFromContentDisposition(contentDisposition);

        saveBlobAsFile(response.data, fileName);
      } catch (error) {
        console.error('Error downloading file by url:', error);
        throw error;
      }
    },
  },
};
